import { type MaybeRefOrGetter, toValue } from 'vue';
import { analytics } from '@/services/Analytics';
import { photoProcessing } from '@/services/PhotoProcessing';
import { sentryService } from '@/services/telemetry/sentry';
import { contentImageUrlWithoutSize } from '@/shared/utils/content-image-url';
import { fetchParallelBlob } from '@/shared/utils/fetch-parallel';
import {
  errorMessage,
  warnTelemetryFailure,
} from '@/shared/utils/track-telemetry';
import type { CustomerPortraitDownloadItem } from './customerPortraitDownloadTypes';

interface CustomerPortraitShareCopy {
  text: string;
  title: string;
}

interface UseCustomerPortraitDeliveryOptions {
  abortSignal: MaybeRefOrGetter<AbortSignal | undefined>;
  getImageElement: (index: number) => HTMLImageElement | null;
  isInternalApiUrl: (url: string | undefined) => boolean;
  isUpscaledUrl: (url: string | undefined) => boolean;
  isUnmounted: MaybeRefOrGetter<boolean>;
  portraitCurrentUrlByUuid: MaybeRefOrGetter<Record<string, string>>;
  updatePortrait: (
    uuid: string,
    diff: Partial<CustomerPortraitDownloadItem>
  ) => void;
}

export interface CustomerPortraitDelivery {
  detectShareMenuSupport: () => 'files' | 'url' | null;
  downloadPortrait: (portrait: CustomerPortraitDownloadItem) => Promise<void>;
  fetchPortraitBlobPreferringUpscaled: (
    portrait: CustomerPortraitDownloadItem
  ) => Promise<Blob>;
  portraitToBlob: (
    portrait: CustomerPortraitDownloadItem,
    index: number
  ) => Promise<Blob>;
  sharePortrait: (
    portrait: CustomerPortraitDownloadItem,
    index: number,
    copy: CustomerPortraitShareCopy
  ) => Promise<void>;
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    error.name === 'AbortError'
  );
}

type NavigatorShare = (data?: ShareData) => Promise<void>;

function navigatorShare(): NavigatorShare | undefined {
  return (navigator as unknown as { share?: NavigatorShare }).share;
}

function absoluteUrl(url: string): string {
  return new URL(url, window.location.href).href;
}

async function fetchFullBlob(
  url: string,
  signal: AbortSignal | undefined
): Promise<Blob> {
  const response = await fetch(url, {
    cache: 'no-store',
    signal,
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} for ${url}`);
  }
  return response.blob();
}

async function copyText(value: string): Promise<boolean> {
  let textarea: HTMLTextAreaElement | null = null;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }

    textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    textarea?.remove();
  }
}

type AnalyticsMetricMethod =
  | typeof analytics.trackIncrement
  | typeof analytics.trackTimeTo;

function callAnalyticsMetricWithOldArity(
  method: AnalyticsMetricMethod,
  metric: string
): void {
  Reflect.apply(method, analytics, [metric]);
}

export function useCustomerPortraitDelivery(
  options: UseCustomerPortraitDeliveryOptions
): CustomerPortraitDelivery {
  function currentUrl(portrait: CustomerPortraitDownloadItem): string {
    return (
      toValue(options.portraitCurrentUrlByUuid)[portrait.uuid] || portrait.url
    );
  }

  // The caller may hand over the gallery's display item, whose url already
  // carries the width/quality display tier — that tier must not leak into an
  // archival download (KIOSK-FRONTEND-112). Strip it, and strip any variant
  // so the request lands on the v1 default: upscaled when one exists,
  // generated otherwise, resolved server-side in a single round trip.
  function archivalUrl(rawUrl: string): string {
    const url = contentImageUrlWithoutSize(rawUrl);
    if (!url.includes('/api/v1/content/images/')) {
      return url;
    }
    const splitAt = url.indexOf('?');
    const path = splitAt === -1 ? url : url.slice(0, splitAt);
    const query = splitAt === -1 ? '' : url.slice(splitAt + 1);
    const rest = query
      .split('&')
      .filter((param) => param && !param.startsWith('variant='));
    return rest.length ? `${path}?${rest.join('&')}` : path;
  }

  async function fetchPortraitBlobPreferringUpscaled(
    portrait: CustomerPortraitDownloadItem
  ): Promise<Blob> {
    const contentUrl = archivalUrl(portrait.url);
    if (contentUrl.includes('/api/v1/content/images/')) {
      const init = {
        cache: 'no-store',
        signal: toValue(options.abortSignal),
      } as const;
      try {
        return await fetchParallelBlob(contentUrl, init);
      } catch (error) {
        // The candidate loop this replaced tried a second URL on any
        // non-abort failure, and for every URL the portal actually emits
        // that second candidate resolved to the same request — so it was, in
        // practice, a retry. `fetchParallelBlob` does not cover this itself:
        // its per-chunk retry loop starts AFTER the first Range probe, which
        // is a bare fetch. Losing that absorber turned an invisible blip on
        // venue wifi into a visible failure on the keepsake path.
        if (isAbortError(error)) {
          throw error;
        }
        return await fetchParallelBlob(contentUrl, init);
      }
    }
    return await fetchFullBlob(contentUrl, toValue(options.abortSignal));
  }

  async function downloadPortrait(
    portrait: CustomerPortraitDownloadItem
  ): Promise<void> {
    if (portrait.isDownloading || toValue(options.isUnmounted)) {
      return;
    }

    try {
      callAnalyticsMetricWithOldArity(
        analytics.trackIncrement,
        analytics.METRICS.download_download_clicked
      );
      callAnalyticsMetricWithOldArity(
        analytics.trackTimeTo,
        analytics.METRICS.time_download_used
      );
    } catch {
      // Analytics failure must not block a customer-initiated download.
    }

    const { uuid } = portrait;
    const url = currentUrl(portrait);
    const isUpscaled = options.isUpscaledUrl(url);
    sentryService.addBreadcrumb(
      'download',
      'Portrait download initiated',
      {
        uuid,
        is_upscaled: isUpscaled,
        url_type: options.isInternalApiUrl(url) ? 'internal' : 'external',
      },
      'info'
    );

    options.updatePortrait(uuid, {
      downloadError: false,
      isDownloading: true,
    });
    try {
      const blob = await fetchPortraitBlobPreferringUpscaled(portrait);
      const blobUrl = URL.createObjectURL(blob);
      try {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `${uuid}.jpg`;
        link.style.display = 'none';
        document.body.appendChild(link);
        try {
          link.click();
        } finally {
          link.remove();
        }
      } finally {
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      }
      sentryService.addBreadcrumb(
        'download',
        'Portrait download completed',
        { uuid, blob_size: blob.size },
        'info'
      );
      options.updatePortrait(uuid, { downloadError: false });
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }
      sentryService.captureException(error, {
        tags: {
          component: 'CustomerPortraitsDownloadCode',
          operation: 'download_portrait',
        },
        extra: {
          uuid,
          is_upscaled: isUpscaled,
          url_type: options.isInternalApiUrl(url) ? 'internal' : 'external',
        },
      });
      options.updatePortrait(uuid, { downloadError: true });
    } finally {
      options.updatePortrait(uuid, { isDownloading: false });
    }
  }

  async function portraitToBlob(
    portrait: CustomerPortraitDownloadItem,
    index: number
  ): Promise<Blob> {
    const imageElement = options.getImageElement(index);
    if (imageElement) {
      try {
        return await photoProcessing.canvasToJpegBlob(
          photoProcessing.drawMediaOnCanvas(imageElement)
        );
      } catch (error) {
        sentryService.addBreadcrumb(
          'share',
          'Canvas conversion failed, falling back to fetch',
          { uuid: portrait.uuid, error: errorMessage(error) },
          'warning'
        );
      }
    }

    const url = currentUrl(portrait);
    try {
      return await fetchPortraitBlobPreferringUpscaled(portrait);
    } catch (error) {
      if (isAbortError(error)) {
        throw error;
      }
      sentryService.addBreadcrumb(
        'share',
        'Portrait blob fetch failed',
        {
          uuid: portrait.uuid,
          error: errorMessage(error),
          url_type: options.isInternalApiUrl(url) ? 'internal' : 'external',
        },
        'error'
      );
      sentryService.incrementCounter('download.share_fetch_errors', 1);
      throw error;
    }
  }

  function detectShareMenuSupport(): 'files' | 'url' | null {
    try {
      const sampleFile = new File([''], 'sample.jpg', { type: 'image/jpeg' });
      if (navigator.canShare?.({ files: [sampleFile] })) {
        return 'files';
      }
      if (navigatorShare()) {
        return 'url';
      }
    } catch (error) {
      sentryService.addBreadcrumb(
        'share',
        'Share detection failed',
        { error: errorMessage(error) },
        'warning'
      );
    }
    return null;
  }

  function trackCompletedShare(shareType: 'files' | 'url'): void {
    sentryService.incrementCounter('download.share_completed', 1, {
      share_type: shareType,
    });
  }

  function recordNavigatorShareFailure(
    portrait: CustomerPortraitDownloadItem,
    shareType: 'files' | 'url',
    error: unknown
  ): void {
    warnTelemetryFailure('[Share] Failed:', error);
    sentryService.addBreadcrumb(
      'share',
      'Navigator share failed',
      {
        uuid: portrait.uuid,
        error: errorMessage(error),
        share_type: shareType,
      },
      'warning'
    );
  }

  async function shareWithNavigator(
    data: ShareData,
    shareType: 'files' | 'url',
    onError: (error: unknown) => void
  ): Promise<boolean> {
    const share = navigatorShare();
    if (!share) {
      return false;
    }
    try {
      await share.call(navigator, data);
      trackCompletedShare(shareType);
      return true;
    } catch (error) {
      if (isAbortError(error)) {
        return true;
      }
      onError(error);
      return false;
    }
  }

  async function shareFile(
    portrait: CustomerPortraitDownloadItem,
    index: number,
    url: string,
    copy: CustomerPortraitShareCopy
  ): Promise<boolean> {
    let blob: Blob;
    try {
      blob = await portraitToBlob(portrait, index);
    } catch (error) {
      if (isAbortError(error)) {
        return true;
      }
      sentryService.addBreadcrumb(
        'share',
        'File share failed, blob creation error',
        { uuid: portrait.uuid, error: errorMessage(error) },
        'error'
      );
      return shareWithNavigator({ url, ...copy }, 'url', (fallbackError) =>
        warnTelemetryFailure('[Share] URL fallback failed:', fallbackError)
      );
    }

    const file = new File([blob], `${portrait.uuid}.jpg`, {
      type: 'image/jpeg',
    });
    return shareWithNavigator({ files: [file], ...copy }, 'files', (error) =>
      recordNavigatorShareFailure(portrait, 'files', error)
    );
  }

  async function sharePortrait(
    portrait: CustomerPortraitDownloadItem,
    index: number,
    copy: CustomerPortraitShareCopy
  ): Promise<void> {
    try {
      callAnalyticsMetricWithOldArity(
        analytics.trackIncrement,
        analytics.METRICS.download_send_or_share_clicked
      );
      callAnalyticsMetricWithOldArity(
        analytics.trackTimeTo,
        analytics.METRICS.time_download_used
      );
    } catch {
      // Analytics failure must not block the share action.
    }

    const url = absoluteUrl(currentUrl(portrait));
    const shareType = detectShareMenuSupport();
    options.updatePortrait(portrait.uuid, { shareStatus: null });
    sentryService.addBreadcrumb(
      'download',
      'Portrait share initiated',
      { uuid: portrait.uuid, share_type: shareType || 'unsupported' },
      'info'
    );

    let completed = false;
    if (shareType === 'files') {
      completed = await shareFile(portrait, index, url, copy);
    } else if (shareType === 'url') {
      completed = await shareWithNavigator({ url, ...copy }, 'url', (error) =>
        recordNavigatorShareFailure(portrait, 'url', error)
      );
    }

    if (!completed && (await copyText(url))) {
      options.updatePortrait(portrait.uuid, { shareStatus: 'copied' });
      sentryService.incrementCounter('download.share_completed', 1, {
        share_type: 'copy',
      });
      return;
    }

    if (!completed) {
      options.updatePortrait(portrait.uuid, { shareStatus: 'error' });
    }
  }

  return {
    detectShareMenuSupport,
    downloadPortrait,
    fetchPortraitBlobPreferringUpscaled,
    portraitToBlob,
    sharePortrait,
  };
}
